import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { jsPDF } from 'jspdf'
import { sanitizeSopHtml } from '../../lib/sop-sanitize'
import {
  AlertTriangle,
  BookOpen,
  Download,
  ExternalLink,
  FileText,
  Plug,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { isStructuredTemplate, sopContentFallback } from '../../lib/sop-content'
import { pdfSafeText } from '../../lib/pdf-text'
import { htmlToBlocks, renderTable } from '../../lib/sop-pdf-blocks'
import { formatDate, formatDateTime } from '../../lib/format'
import { Breadcrumb } from '../../components/Breadcrumb'
import { FilterRow } from '../../components/filters/FilterRow'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import { sopLibraryResponse } from '../../lib/types'
import { useAuthStore } from '../../stores/authStore'
import { SopFlowDiagram, reactflowToText } from '../../components/SopFlowDiagram'

/**
 * /sops-library — the SOPs Library page is now a read-only window into
 * the tenant's connected **SOP Galaxy** integration. Locally-uploaded
 * SOPs (the per-list upload flow inside individual Spaces) are no
 * longer surfaced here; this page exists solely to expose the catalog
 * that comes back over the external integration. When the tenant
 * hasn't configured SOP Galaxy yet the page renders a connect prompt
 * (admins) or an admin-handoff note (everyone else).
 *
 * Data shape:
 *   GET /sops/library →
 *     { data: SopLibraryEntry[], connected: boolean, error?: string }
 *
 * The page is identical for Company Admin (/sops-library) and User
 * panel (/app/sops-library) — both routes mount this component. Only
 * the connect-prompt CTA differs based on role.
 */
export function SopsPage() {
  const role = useAuthStore((s) => s.user?.role)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('updated')
  // `'all'` shows every department; otherwise this is a departmentId
  // (or the literal '__none__' sentinel for SOPs the upstream returned
  // without a department).
  const [departmentFilter, setDepartmentFilter] = useState('all')
  // Currently-open SOP detail. Holds the *list-row* version of the SOP
  // (so the modal can show title/department immediately while content
  // streams in) and a separate query fetches the body. `null` = closed.
  const [openSop, setOpenSop] = useState(null)

  const query = useQuery({
    queryKey: ['sops', 'library'],
    queryFn: async () => {
      const res = await api.get('/sops/library')
      return sopLibraryResponse.parse(res.data)
    },
    // Re-fetch when window regains focus — admins typically toggle
    // between this page and the SOP Integration settings, and we
    // want a freshly-saved endpoint to show up without a manual
    // reload.
    refetchOnWindowFocus: true,
  })

  const all = query.data?.data ?? []
  const connected = query.data?.connected ?? false
  const upstreamError = query.data?.error ?? null

  // Build the department picker options off the FULL list (not the
  // filtered one) so picking a department doesn't make the other
  // departments vanish from the dropdown. "Uncategorised" is only
  // offered when at least one SOP actually has no department.
  const departmentOptions = useMemo(() => {
    const seen = new Map()
    let hasOrphans = false
    for (const sop of all) {
      if (sop.departmentId) {
        if (!seen.has(sop.departmentId)) {
          seen.set(sop.departmentId, sop.departmentName ?? 'Untitled')
        }
      } else {
        hasOrphans = true
      }
    }
    const opts = Array.from(seen, ([id, name]) => ({ value: id, label: name }))
    opts.sort((a, b) => a.label.localeCompare(b.label))
    if (hasOrphans) opts.push({ value: '__none__', label: 'Uncategorised' })
    return [{ value: 'all', label: 'All departments' }, ...opts]
  }, [all])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let list = all
    if (departmentFilter !== 'all') {
      list = list.filter((s) => {
        const key = s.departmentId ?? '__none__'
        return key === departmentFilter
      })
    }
    if (q) {
      list = list.filter((s) => {
        const hay = `${s.title} ${s.summary ?? ''} ${s.departmentName ?? ''}`.toLowerCase()
        return hay.includes(q)
      })
    }
    list = list.slice() // copy before sort
    if (sortBy === 'title') {
      list.sort((a, b) => a.title.localeCompare(b.title))
    } else {
      list.sort((a, b) => {
        const da = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
        const db = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
        return db - da
      })
    }
    return list
  }, [all, search, sortBy, departmentFilter])

  // Bucket the (search-filtered, sort-ordered) SOPs by department so
  // the page renders one section per department with a coloured chip
  // header. SOPs without a department fall into a single
  // "Uncategorised" bucket, always parked last.
  //
  // Section order follows the active Sort so the control reorders the
  // WHOLE page, not just the cards inside a section:
  //   • Sort: Updated → sections ordered by their most-recently-updated
  //     SOP (freshest section first). Previously sections were ALWAYS
  //     alphabetical, which stranded the newest SOP at the bottom of the
  //     page whenever its department sorted late in the alphabet.
  //   • Sort: Title → sections stay alphabetical by department name for a
  //     stable, navigable structure (cards within are already A→Z).
  const grouped = useMemo(() => {
    const buckets = new Map()
    for (const sop of filtered) {
      const key = sop.departmentId ?? '__none__'
      if (!buckets.has(key)) {
        buckets.set(key, {
          id: sop.departmentId ?? null,
          name: sop.departmentName ?? 'Uncategorised',
          color: sop.departmentColor ?? null,
          sops: [],
        })
      }
      buckets.get(key).sops.push(sop)
    }
    const list = Array.from(buckets.values())
    list.sort((a, b) => {
      // Uncategorised always last, regardless of sort.
      if (!a.id && b.id) return 1
      if (a.id && !b.id) return -1
      if (sortBy === 'updated') {
        // `filtered` is updated-desc, so each bucket's first card is its
        // newest — order sections by that so the freshest section leads.
        const da = a.sops[0]?.updatedAt
          ? new Date(a.sops[0].updatedAt).getTime()
          : 0
        const db = b.sops[0]?.updatedAt
          ? new Date(b.sops[0].updatedAt).getTime()
          : 0
        if (db !== da) return db - da
      }
      // Title sort — and the recency tiebreak — fall back to name.
      return a.name.localeCompare(b.name)
    })
    return list
  }, [filtered, sortBy])

  const lastUpdatedAt = useMemo(() => {
    if (all.length === 0) return null
    return all.reduce(
      (acc, s) =>
        s.updatedAt && (!acc || s.updatedAt > acc) ? s.updatedAt : acc,
      null,
    )
  }, [all])

  return (
    <div>
      <Breadcrumb current="SOPs Library" />

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <BookOpen
              size={22}
              className="text-[color:var(--color-accent-strong)]"
            />
            SOPs Library
          </h1>
          <p className="text-sm text-[color:var(--color-ink-muted)] mt-1">
            {connected ? (
              <>
                {all.length} SOP{all.length === 1 ? '' : 's'} synced from SOP Galaxy
                {lastUpdatedAt
                  ? ` · last updated ${formatDateTime(lastUpdatedAt)}`
                  : ''}
              </>
            ) : (
              <>Reference library from your connected SOP Galaxy workspace.</>
            )}
          </p>
        </div>
        {connected ? (
          <ConnectedChip />
        ) : null}
      </div>

      {/* Filter bar — only meaningful when the integration is live
       *  AND the catalog has rows. Hide it for the connect prompt so
       *  the page reads cleanly. */}
      {connected && all.length > 0 ? (
        <FilterRow>
          <FilterRow.Search
            value={search}
            onChange={setSearch}
            placeholder="Search SOPs"
          />
          {/* Only show the Department picker when the upstream actually
           *  carried department info — a flat catalogue with no
           *  departments would render a useless one-option dropdown. */}
          {departmentOptions.length > 1 ? (
            <FilterRow.Select
              ariaLabel="Filter by department"
              value={departmentFilter}
              onChange={setDepartmentFilter}
              options={departmentOptions.map((opt) =>
                opt.value === 'all'
                  ? opt
                  : { value: opt.value, label: `Dept: ${opt.label}` },
              )}
            />
          ) : null}
          <FilterRow.Select
            ariaLabel="Sort by"
            value={sortBy}
            onChange={setSortBy}
            options={[
              { value: 'updated', label: 'Sort: Updated' },
              { value: 'title', label: 'Sort: Title' },
            ]}
          />
          {/* Clear-all chip — appears when any control is off its default
           *  (search text, a chosen department, or a non-default sort) and
           *  resets all three at once. `boxed` matches the bordered-pill
           *  look of the Search / Dept / Sort controls, consistent with the
           *  Files & Links and platform list pages. */}
          <FilterRow.Clear
            boxed
            show={
              search.trim() !== '' ||
              departmentFilter !== 'all' ||
              sortBy !== 'updated'
            }
            onClick={() => {
              setSearch('')
              setDepartmentFilter('all')
              setSortBy('updated')
            }}
          />
        </FilterRow>
      ) : null}

      {query.isLoading ? (
        <div className="text-[color:var(--color-ink-muted)]">
          Loading SOP library…
        </div>
      ) : query.isError ? (
        <div className="text-red-600 text-sm">
          {extractApiError(query.error).message}
        </div>
      ) : !connected ? (
        <DisconnectedState role={role} upstreamError={upstreamError} />
      ) : filtered.length === 0 ? (
        <EmptyConnectedState hasAny={all.length > 0} />
      ) : (
        <div className="space-y-8">
          {grouped.map((group) => (
            <DepartmentSection
              key={group.id ?? '__none__'}
              group={group}
              onOpen={setOpenSop}
            />
          ))}
        </div>
      )}

      <SopDetailDialog
        sop={openSop}
        onClose={() => setOpenSop(null)}
      />
    </div>
  )
}

/** Modal that paints SOP metadata + full content directly from the
 *  catalogue row. No detail-endpoint round-trip: the catalogue ships
 *  every field we need (when configured with `include_content: true`
 *  on the upstream), so the dialog opens instantly and never spins.
 *  Render is fully driven by `sop.content` — either a flat text shape
 *  (`structured.type === 'text'`) or a structured template shape
 *  (`structured.type === 'structured'`, with header + sections). */
function SopDetailDialog({ sop, onClose }) {
  const open = Boolean(sop)
  const [downloading, setDownloading] = useState(false)

  // Download the SOP as a PDF file. Prefer the inline catalogue
  // content; when the upstream didn't ship it, fall back to the detail
  // endpoint for the flattened body. Best-effort — a detail miss still
  // exports whatever metadata we have rather than failing the click.
  async function handleDownload() {
    if (!sop) return
    setDownloading(true)
    try {
      let override
      if (!flattenSopContent(sop.content)) {
        try {
          const res = await api.get(
            `/sops/library/${encodeURIComponent(sop.slug || sop.id)}`,
          )
          override = { plain_text: String(res.data?.content ?? '') }
        } catch {
          // Ignore — fall back to a metadata-only export.
        }
      }
      downloadSopFile(sop, override)
    } finally {
      setDownloading(false)
    }
  }

  if (!sop) {
    // Render nothing rather than mounting an empty Modal — keeps the
    // body-scroll-lock effect inside Modal from firing on close.
    return null
  }

  const accent = sop.departmentColor || 'var(--color-ink-muted)'

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={sop.title}
      description={
        sop.departmentName ? (
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: accent }}
              aria-hidden="true"
            />
            {sop.departmentName}
            {sop.updatedAt ? ` · Updated ${formatDateTime(sop.updatedAt)}` : ''}
          </span>
        ) : sop.updatedAt ? (
          `Updated ${formatDate(sop.updatedAt)}`
        ) : undefined
      }
      size="xl"
      footer={
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleDownload} loading={downloading}>
            <Download size={14} />
            Download
          </Button>
        </div>
      }
      dismissOnBackdrop
    >
      <SopDetailBody sop={sop} />
    </Modal>
  )
}

/** Pure metadata renderer for the SOP detail dialog. Reads everything
 *  off the catalogue row (`sop` prop) — no API calls. Two-column
 *  responsive grid of labelled facts with summary + tags + compliance
 *  callouts beneath. Any field the upstream didn't ship gets quietly
 *  dropped (no "Unknown" placeholders cluttering the layout). */
function SopDetailBody({ sop }) {
  const facts = []
  if (sop.status) facts.push({ label: 'Status', value: titleCase(sop.status) })
  if (sop.priority) facts.push({ label: 'Priority', value: titleCase(sop.priority) })
  if (sop.category) facts.push({ label: 'Category', value: sop.category })
  if (sop.version != null) {
    facts.push({
      label: 'Version',
      value: sop.versionLabel
        ? `${sop.versionLabel} (v${sop.version})`
        : `v${sop.version}`,
    })
  }
  if (sop.owner?.name) {
    facts.push({
      label: 'Owner',
      value: sop.owner.email
        ? `${sop.owner.name} · ${sop.owner.email}`
        : sop.owner.name,
    })
  }
  if (sop.publishedAt) facts.push({ label: 'Published', value: formatDateTime(sop.publishedAt) })
  if (sop.implementedAt) facts.push({ label: 'Implemented', value: formatDateTime(sop.implementedAt) })
  if (sop.createdAt) facts.push({ label: 'Created', value: formatDateTime(sop.createdAt) })

  return (
    <div className="space-y-5">
      {sop.isCritical ? (
        <div className="rounded-lg border border-[color:var(--color-priority-urgent)]/30 bg-[color:var(--color-priority-urgent)]/5 px-3 py-2 text-sm text-[color:var(--color-priority-urgent)] inline-flex items-start gap-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>Marked critical — review carefully before deviating.</span>
        </div>
      ) : null}

      {sop.summary ? (
        <p className="text-sm leading-6 text-[color:var(--color-ink)]">
          {sop.summary}
        </p>
      ) : null}

      {facts.length > 0 ? (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {facts.map((f) => (
            <div key={f.label} className="flex items-baseline gap-2">
              <dt className="text-[color:var(--color-ink-muted)] shrink-0">
                {f.label}:
              </dt>
              <dd className="text-[color:var(--color-ink-strong)] font-medium m-0">
                {f.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {sop.tags && sop.tags.length > 0 ? (
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1.5">
            Tags
          </p>
          <div className="flex flex-wrap gap-1.5">
            {sop.tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {sop.complianceMappings && sop.complianceMappings.length > 0 ? (
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1.5">
            Compliance
          </p>
          <div className="flex flex-wrap gap-1.5">
            {sop.complianceMappings.map((c) => (
              <span
                key={c}
                className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-[#EDE9FE] text-[#6D28D9] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <SopContentBlock content={sop.content} />

      <div className="flex items-center gap-1.5 text-xs text-[color:var(--color-ink-muted)] pt-1">
        <ExternalLink size={12} aria-hidden="true" />
        Synced from SOP Galaxy
      </div>
    </div>
  )
}

/** Renders the SOP body from the catalogue-embedded `content` blob.
 *  The blob has two known shapes (per the upstream catalogue):
 *
 *    1. Flat text  — `structured.type === 'text'`, single `content` string
 *       that's typically Markdown. Rendered with whitespace preserved
 *       so headings (`## Purpose`) and bullets still read as lists.
 *
 *    2. Structured — `structured.type === 'structured'`, an SOP template
 *       with a `header` (sopNumber / version / date / department) and
 *       a `sections` map. Each section value can be HTML string, an
 *       array of `{role,responsibility}` / `{keyword,definition}` /
 *       `{procedureName,task,personResponsible}` rows, or a `flowchart`
 *       `{url,caption,fileName}` object. Empty/blank sections are
 *       skipped silently so the dialog doesn't read as half-filled.
 *
 *  When the upstream omits `content` entirely (older catalogues), we
 *  render the flat text-only path with the catalogue's `summary` as a
 *  graceful fallback. */
function SopContentBlock({ content }) {
  if (!content || typeof content !== 'object') return null
  const structured = content.structured
  if (!structured || typeof structured !== 'object') {
    // No structured shape — fall back to the plain_text dump if any.
    const plain = typeof content.plain_text === 'string' ? content.plain_text : ''
    if (!plain.trim()) return null
    return (
      <div className="border-t border-[color:var(--color-rule)] pt-4">
        <SectionHeading label="Content" />
        <div className="whitespace-pre-wrap text-sm leading-6 text-[color:var(--color-ink)]">
          {plain}
        </div>
      </div>
    )
  }

  // Shape 1 — flat text. Most concise path; render once and exit.
  if (structured.type === 'text') {
    const text = typeof structured.content === 'string' ? structured.content : ''
    if (!text.trim()) return null
    return (
      <div className="border-t border-[color:var(--color-rule)] pt-4">
        <SectionHeading label="Content" />
        <div className="whitespace-pre-wrap text-sm leading-6 text-[color:var(--color-ink)]">
          {text}
        </div>
      </div>
    )
  }

  // Shape 2b — a rich-text document (TipTap `doc` / `tiptap-block` / bare array)
  // from the block editor (the DEFAULT SOP editor). It's an OBJECT but has no
  // {header, sections}, so the structured-template renderer below would show an
  // empty body. Render the envelope's already-rendered HTML (sanitized) — or its
  // plain_text — which SOP Galaxy ships alongside `structured`. This is the fix
  // for the "metadata shows but content is blank" report.
  if (!isStructuredTemplate(structured)) {
    const { html, text } = sopContentFallback(content)
    if (html) {
      return (
        <div className="border-t border-[color:var(--color-rule)] pt-4">
          <SectionHeading label="Content" />
          <div
            className="text-sm leading-6 text-[color:var(--color-ink)] sop-prose overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: sanitizeSopHtml(html) }}
          />
        </div>
      )
    }
    if (text) {
      return (
        <div className="border-t border-[color:var(--color-rule)] pt-4">
          <SectionHeading label="Content" />
          <div className="whitespace-pre-wrap text-sm leading-6 text-[color:var(--color-ink)]">
            {text}
          </div>
        </div>
      )
    }
    return null
  }

  // Shape 2 — structured template. Header strip + every non-empty
  // section, in the canonical SOP order, then any unknown / custom
  // (`sec_*`) keys at the end so the renderer is forward-compatible
  // with future template fields the catalogue might add.
  const header = structured.header && typeof structured.header === 'object'
    ? structured.header
    : null
  const sections = structured.sections && typeof structured.sections === 'object'
    ? structured.sections
    : {}
  const customDefs = Array.isArray(structured.customSectionDefs)
    ? structured.customSectionDefs
    : []

  const orderedKeys = SOP_SECTION_ORDER
  const orderedSet = new Set(orderedKeys)
  // Keep custom / future keys (e.g. `sec_1777287709738_xuv4`) so admins
  // who templated extra sections don't silently lose them.
  const extraKeys = Object.keys(sections).filter((k) => !orderedSet.has(k))
  const allKeys = [...orderedKeys, ...extraKeys]

  return (
    <div className="border-t border-[color:var(--color-rule)] pt-4 space-y-5">
      {header ? <SopHeaderStrip header={header} /> : null}
      {allKeys.map((key) => (
        <SopSection
          key={key}
          name={key}
          value={sections[key]}
          customDefs={customDefs}
        />
      ))}
    </div>
  )
}

/** Render the small "SOP Number · Version · Date · Department" strip
 *  shown at the top of a structured SOP. Mirrors the printable
 *  header most SOP-management tools put on the first page. */
function SopHeaderStrip({ header }) {
  const items = []
  if (header.sopNumber) items.push({ label: 'SOP Number', value: header.sopNumber })
  if (header.version) items.push({ label: 'Version', value: header.version })
  if (header.date) items.push({ label: 'Date', value: header.date })
  if (header.department) items.push({ label: 'Department', value: header.department })
  if (items.length === 0) return null
  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-y-2 gap-x-4 text-xs">
      {items.map((i) => (
        <div key={i.label}>
          <p className="text-[color:var(--color-ink-muted)] uppercase tracking-wider font-semibold m-0">
            {i.label}
          </p>
          <p className="text-[color:var(--color-ink-strong)] font-medium m-0 mt-0.5">
            {i.value}
          </p>
        </div>
      ))}
    </div>
  )
}

/** Render one section of a structured SOP. Dispatches on value type:
 *    - empty / blank        → skip
 *    - flowchart object     → download link
 *    - array of rows        → table (uses first row's keys for headers)
 *    - HTML / text string   → dangerouslySetInnerHTML, sanitised by
 *                              sanitizeSopHtml (NOT fully trusted)
 *  Unknown shapes get a JSON-stringified fallback so nothing is silently
 *  swallowed during debugging. */
function SopSection({ name, value, customDefs }) {
  if (isEmptySection(value)) return null
  const label = resolveSectionLabel(name, customDefs)

  // Flowchart shape — `{ url, caption, fileName, mimeType, sizeBytes }`.
  if (value && typeof value === 'object' && !Array.isArray(value) && 'url' in value) {
    if (!value.url) return null
    return (
      <div>
        <SectionHeading label={label} />
        <a
          href={value.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-[color:var(--color-accent-strong)] underline underline-offset-2 hover:opacity-80"
        >
          <ExternalLink size={14} />
          {value.fileName || value.caption || 'Open flowchart'}
        </a>
        {value.caption && value.caption !== value.fileName ? (
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            {value.caption}
          </p>
        ) : null}
      </div>
    )
  }

  // Array of row objects — render as a small table. Each row's own
  // keys become the column headers (title-cased) so we don't have to
  // hard-code role/responsibility, keyword/definition,
  // procedureName/task/personResponsible etc. one by one.
  if (Array.isArray(value)) {
    const rows = value.filter((r) => r && typeof r === 'object' && Object.values(r).some((v) => String(v ?? '').trim()))
    if (rows.length === 0) return null
    const cols = Object.keys(rows[0])
    return (
      <div>
        <SectionHeading label={label} />
        <div className="overflow-x-auto rounded-lg border border-[color:var(--color-rule)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--color-canvas)]">
              <tr>
                {cols.map((c) => (
                  <th
                    key={c}
                    className="text-left px-3 py-2 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]"
                  >
                    {humanise(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={i}
                  className="border-t border-[color:var(--color-rule)] align-top"
                >
                  {cols.map((c) => (
                    <td key={c} className="px-3 py-2 text-[color:var(--color-ink)]">
                      {String(r[c] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // String — the upstream's rich-text HTML. SOPs are tenant content, but
  // `sop.create` is an EDIT-level operation (Managers / Team Leaders, not
  // just Company Admins, can author SOPs) and content can be AI-extracted
  // from an uploaded document — so the HTML is NOT fully trusted. Run it
  // through sanitizeSopHtml (DOMPurify + forbidden form/style tags) before
  // `dangerouslySetInnerHTML` so a malicious SOP can't land stored XSS, a
  // phishing form or a page-wide restyle on everyone who views it. Safe
  // formatting (headings, lists, tables, links, bold) is preserved; scripts
  // and event handlers are stripped. Plain strings pass through unchanged.
  if (typeof value === 'string') {
    return (
      <div>
        <SectionHeading label={label} />
        <div
          className="text-sm leading-6 text-[color:var(--color-ink)] sop-prose"
          dangerouslySetInnerHTML={{ __html: sanitizeSopHtml(value) }}
        />
      </div>
    )
  }

  // Flow-diagram shape — `{ type:'reactflow', nodes:[…], edges:[…] }`. Render
  // it as an actual boxes-and-arrows diagram instead of dumping the raw graph
  // JSON (which is what the fallback below used to do).
  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    value.type === 'reactflow' &&
    Array.isArray(value.nodes)
  ) {
    return (
      <div>
        <SectionHeading label={label} />
        <SopFlowDiagram value={value} />
      </div>
    )
  }

  // Unknown shape — last-resort dump so we don't silently drop a
  // future section type. Pretty-printed JSON inside a code block.
  return (
    <div>
      <SectionHeading label={label} />
      <pre className="whitespace-pre-wrap text-xs leading-5 text-[color:var(--color-ink-muted)] bg-[color:var(--color-canvas)] rounded-lg p-3 overflow-x-auto">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  )
}

function SectionHeading({ label }) {
  return (
    <h3 className="font-display text-sm text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 mb-2 uppercase">
      {label}
    </h3>
  )
}

/** Treat empty strings, empty arrays, empty objects, blank HTML
 *  (`<p></p>`, `<p><br></p>`) and `null` as "no content". Keeps the
 *  dialog clean when an SOP only filled in half the template. */
function isEmptySection(v) {
  if (v == null) return true
  if (Array.isArray(v)) return v.length === 0 || v.every((r) => r && typeof r === 'object' && Object.values(r).every((x) => !String(x ?? '').trim()))
  if (typeof v === 'object') {
    return Object.values(v).every((x) => !x || (typeof x === 'string' && !x.trim()))
  }
  if (typeof v === 'string') {
    const stripped = v.replace(/<[^>]+>/g, '').replace(/&nbsp;|&amp;|\s/g, '').trim()
    return stripped.length === 0
  }
  return false
}

/** Section-key → display label. Known canonical sections get
 *  hand-picked titles. Custom `sec_*` keys check the
 *  `customSectionDefs` registry from the upstream first; if that's
 *  empty we fall back to a humanised version of the key. */
function resolveSectionLabel(key, customDefs) {
  const known = SECTION_LABELS[key]
  if (known) return known
  if (Array.isArray(customDefs)) {
    const hit = customDefs.find((d) => d && (d.key === key || d.id === key))
    if (hit?.label) return hit.label
    if (hit?.title) return hit.title
  }
  return humanise(key)
}

const SECTION_LABELS = {
  versionHistory: 'Version History',
  reviewProcedure: 'Review Procedure',
  purpose: 'Purpose',
  scope: 'Scope',
  rolesResponsibilities: 'Roles & Responsibilities',
  keywordsDefinitions: 'Keywords & Definitions',
  riskIdentification: 'Risk Identification',
  procedures: 'Procedures',
  flowchart: 'Flowchart',
  troubleshooting: 'Troubleshooting',
  checklists: 'Checklists',
  serviceContinuity: 'Service Continuity',
  escalations: 'Escalations',
  recordManagement: 'Record Management',
  maintenance: 'Maintenance',
  equipment: 'Equipment',
  changeProcess: 'Change Process',
  references: 'References',
  appendices: 'Appendices',
  signatures: 'Signatures',
}

/** Turn `procedureName` / `personResponsible` / `sec_1777_xuv4` into
 *  human labels. Strips numeric-only custom-key suffixes
 *  (`sec_1777287709738_xuv4` → "Custom Section"), splits camelCase,
 *  and capitalises the first letter of every word. */
function humanise(key) {
  if (/^sec_/.test(key)) return 'Custom Section'
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase())
}

/** Capitalise the first letter and replace underscores with spaces.
 *  Used for `status` / `priority` values that arrive lowercased
 *  (`"published"`, `"high"`) — turns them into display-ready strings
 *  without bringing a `lodash` dependency in for one helper. */
function titleCase(s) {
  const trimmed = String(s ?? '').replace(/_/g, ' ').trim()
  if (!trimmed) return ''
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
}

/** Small "Connected" pill that sits in the top-right when SOP Galaxy
 *  is wired up. Pure status indicator — the actual edit surface lives
 *  on /sop-integration so admins can change endpoint / credentials. */
function ConnectedChip() {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]">
      <span
        className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--color-success)]"
        aria-hidden="true"
      />
      SOP Galaxy connected
    </span>
  )
}

/** Empty state when the integration IS connected but the upstream
 *  catalog returned zero rows (e.g. brand-new SOP Galaxy workspace or
 *  the search filter knocked everything out). */
function EmptyConnectedState({ hasAny }) {
  return (
    <div className="rounded-[12px] border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] py-16 text-center">
      <BookOpen
        size={32}
        className="mx-auto text-[color:var(--color-ink-muted)] mb-3"
      />
      <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
        {hasAny ? 'No SOPs match your search' : 'No SOPs in SOP Galaxy yet'}
      </h3>
      <p className="text-sm text-[color:var(--color-ink-muted)] mt-1">
        {hasAny
          ? 'Try a different search term.'
          : 'Once SOPs are published in SOP Galaxy they will appear here automatically.'}
      </p>
    </div>
  )
}

/** Empty state when the tenant hasn't connected SOP Galaxy. Renders a
 *  different CTA based on the caller's role:
 *    - company_admin → "Connect SOP Galaxy" button → /sop-integration
 *    - everyone else → handoff note (only their admin can connect it)
 *  When the upstream reach failed (configured but unreachable / 4xx /
 *  5xx) the error message bubbles up so the admin can diagnose. */
function DisconnectedState({ role, upstreamError }) {
  const isAdmin = role === 'company_admin'
  return (
    <div className="rounded-[14px] border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] py-14 px-8 text-center max-w-2xl mx-auto">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10 mb-4">
        <Plug size={26} />
      </div>
      <h3 className="font-display text-xl text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
        {upstreamError ? 'SOP Galaxy is unreachable' : 'SOP Galaxy is not connected yet'}
      </h3>
      <p className="text-sm text-[color:var(--color-ink-muted)] mt-2 max-w-md mx-auto">
        {upstreamError
          ? 'Your company is configured to sync from SOP Galaxy, but the upstream service did not respond. Re-check the endpoint or credentials on the SOP Integration page.'
          : 'Your company is not connected to SOP Galaxy yet. Once an admin pairs the integration, every published SOP will appear here automatically — searchable, sortable, ready to read.'}
      </p>

      {/* Surface the raw upstream error so admins can diagnose without
          opening DevTools. Hidden in the unconfigured case to keep the
          empty state clean. */}
      {upstreamError ? (
        <div className="mt-4 inline-flex items-start gap-2 rounded-lg border border-[color:var(--color-priority-urgent)]/30 bg-[color:var(--color-priority-urgent)]/5 px-3 py-2 text-xs text-[color:var(--color-priority-urgent)] text-left max-w-md mx-auto">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span className="break-words">{upstreamError}</span>
        </div>
      ) : null}

      <div className="mt-6">
        {isAdmin ? (
          <Link
            to="/sop-integration"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-white text-sm font-semibold cursor-pointer transition-colors"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            <Plug size={14} />
            {upstreamError ? 'Open SOP Integration settings' : 'Connect SOP Galaxy'}
          </Link>
        ) : (
          <p className="inline-block text-xs text-[color:var(--color-ink-muted)] italic">
            Ask your Company Admin to set up the integration on the SOP
            Integration page.
          </p>
        )}
      </div>
    </div>
  )
}

/** One department's section on the library — coloured dot + name + a
 *  small count chip, then the SOPs in a 3-col grid. The dot uses the
 *  upstream-provided department colour (it lines up with how SOP
 *  Galaxy displays departments) and falls back to a neutral chip when
 *  the upstream didn't carry a colour. */
function DepartmentSection({ group, onOpen }) {
  const accent = group.color || 'var(--color-ink-muted)'
  return (
    <section>
      <div className="flex items-center gap-2.5 mb-3">
        <span
          className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
          style={{ backgroundColor: accent }}
          aria-hidden="true"
        />
        <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
          {group.name}
        </h2>
        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]">
          {group.sops.length}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {group.sops.map((sop) => (
          <SopCard key={sop.id} sop={sop} onOpen={onOpen} />
        ))}
      </div>
    </section>
  )
}

/** Single SOP card. The external catalog only carries title + summary +
 *  updatedAt, so the card is leaner than the previous (locally-uploaded)
 *  version — no list/space/owner stats, no Generate-tasks button. The
 *  card stays clickable so admins can open the SOP's detail surface
 *  later (task-breakdown flow remains on the per-list view). */
function SopCard({ sop, onOpen }) {
  // Deterministic accent colour per SOP so the grid has rhythm.
  const palette = ['#02367B', '#006CA5', '#0496C7', '#04BADE', '#6366f1', '#10b981']
  const hue = (sop.id.charCodeAt(0) + (sop.id.charCodeAt(1) || 0)) % palette.length
  const accent = palette[hue]

  return (
    <button
      type="button"
      onClick={() => onOpen?.(sop)}
      className="group relative w-full text-left rounded-[14px] bg-[color:var(--color-card)] border border-[color:var(--color-rule)] p-5 transition-all hover:shadow-md hover:border-[color:var(--color-accent)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)] cursor-pointer"
    >
      <div className="flex items-start gap-3 mb-2">
        <span
          className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] shrink-0"
          style={{ backgroundColor: `${accent}1f`, color: accent }}
          aria-hidden="true"
        >
          <FileText size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 line-clamp-2">
            {sop.title}
          </h3>
          {sop.updatedAt ? (
            <p className="text-xs text-[color:var(--color-ink-muted)] mt-1">
              Updated {formatDateTime(sop.updatedAt)}
            </p>
          ) : null}
        </div>
      </div>

      {sop.summary ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] mt-2 line-clamp-3">
          {sop.summary}
        </p>
      ) : null}

      <div className="mt-4 flex items-center gap-1.5 text-xs text-[color:var(--color-ink-muted)]">
        <ExternalLink size={12} aria-hidden="true" />
        Synced from SOP Galaxy
      </div>
    </button>
  )
}

// ── SOP download (client-side PDF export) ───────────────────────────
// Canonical structured-section order, shared by the detail renderer
// (SopContentBlock) and the PDF export below so the two never drift.
const SOP_SECTION_ORDER = [
  'versionHistory',
  'reviewProcedure',
  'purpose',
  'scope',
  'rolesResponsibilities',
  'keywordsDefinitions',
  'riskIdentification',
  'procedures',
  'flowchart',
  'troubleshooting',
  'checklists',
  'serviceContinuity',
  'escalations',
  'recordManagement',
  'maintenance',
  'equipment',
  'changeProcess',
  'references',
  'appendices',
  'signatures',
]

/** Strip HTML to readable plain text WITHOUT rendering it — DOMParser
 *  doesn't load resources or run scripts. Regex fallback if unavailable. */
function htmlToText(html) {
  if (typeof html !== 'string') return ''
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return (doc.body?.textContent ?? '')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  } catch {
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  }
}

/** Flatten one structured-SOP section value (HTML/text string, array of
 *  rows, or an attachment object) into plain text for the export. */
function sectionValueToText(value) {
  if (value == null) return ''
  if (typeof value === 'string') return htmlToText(value)
  if (Array.isArray(value)) {
    return value
      .map((row) => {
        if (row == null) return ''
        if (typeof row === 'string') return `- ${htmlToText(row)}`
        if (typeof row === 'object') {
          const pairs = Object.entries(row)
            .filter(([, v]) => v != null && v !== '')
            .map(
              ([k, v]) =>
                `${k}: ${typeof v === 'string' ? htmlToText(v) : String(v)}`,
            )
          return pairs.length ? `- ${pairs.join(' | ')}` : ''
        }
        return `- ${String(row)}`
      })
      .filter(Boolean)
      .join('\n')
  }
  if (typeof value === 'object' && value.url) {
    return `${value.caption || value.fileName || 'Attachment'}: ${value.url}`
  }
  // Flow diagram → readable process order + branches (so downloads / AI input
  // include the flowchart, not a dropped section).
  if (typeof value === 'object' && value.type === 'reactflow' && Array.isArray(value.nodes)) {
    return reactflowToText(value)
  }
  return ''
}

/** Turn a SOP `content` blob (any of the three catalogue shapes) into a
 *  markdown/plain-text body. Returns '' when nothing is renderable so
 *  the caller can fall back to the detail endpoint. */
function flattenSopContent(content) {
  if (!content || typeof content !== 'object') return ''
  const structured = content.structured
  if (!structured || typeof structured !== 'object') {
    return typeof content.plain_text === 'string' ? content.plain_text.trim() : ''
  }
  if (structured.type === 'text') {
    return typeof structured.content === 'string' ? structured.content.trim() : ''
  }
  // Rich-text document (TipTap doc / tiptap-block / array) — not a {header,
  // sections} template, so flatten to the envelope's plain_text (same fix as the
  // renderer, so the Download/PDF body isn't blank for block-editor SOPs).
  if (!isStructuredTemplate(structured)) {
    return typeof content.plain_text === 'string' ? content.plain_text.trim() : ''
  }
  const out = []
  const header =
    structured.header && typeof structured.header === 'object'
      ? structured.header
      : null
  if (header) {
    const hp = []
    if (header.sopNumber) hp.push(`SOP Number: ${header.sopNumber}`)
    if (header.version) hp.push(`Version: ${header.version}`)
    if (header.date) hp.push(`Date: ${header.date}`)
    if (header.department) hp.push(`Department: ${header.department}`)
    if (hp.length) out.push(hp.join('\n'))
  }
  const sections =
    structured.sections && typeof structured.sections === 'object'
      ? structured.sections
      : {}
  const customDefs = Array.isArray(structured.customSectionDefs)
    ? structured.customSectionDefs
    : []
  const orderedSet = new Set(SOP_SECTION_ORDER)
  const allKeys = [
    ...SOP_SECTION_ORDER,
    ...Object.keys(sections).filter((k) => !orderedSet.has(k)),
  ]
  let hasSectionText = false
  for (const key of allKeys) {
    const value = sections[key]
    if (isEmptySection(value)) continue
    const text = sectionValueToText(value).trim()
    if (text) {
      out.push(`## ${resolveSectionLabel(key, customDefs)}\n${text}`)
      hasSectionText = true
    }
  }
  // Sections produced no text — the SOP-number header alone isn't content, so
  // fall back to the full plain_text body (kept beneath the SOP-number line).
  if (!hasSectionText) {
    const plain = typeof content.plain_text === 'string' ? content.plain_text.trim() : ''
    if (plain) out.push(plain)
  }
  return out.join('\n\n').trim()
}

/** Flatten one structured-SOP section value into PDF blocks. Mirrors
 *  sectionValueToText, but an array-of-rows (and an HTML string that contains a
 *  <table>) becomes a `table` block so it renders as a real grid — matching the
 *  on-screen SopContentBlock — instead of being collapsed to text. */
function sectionToBlocks(value) {
  if (value == null) return []
  if (typeof value === 'string') {
    if (/<table\b/i.test(value)) return htmlToBlocks(value)
    const t = htmlToText(value)
    return t ? [{ type: 'text', text: t }] : []
  }
  if (Array.isArray(value)) {
    const rows = value.filter(
      (r) => r && typeof r === 'object' && Object.values(r).some((v) => String(v ?? '').trim()),
    )
    if (rows.length > 0) {
      const cols = Object.keys(rows[0])
      return [
        {
          type: 'table',
          headers: cols.map(humanise),
          rows: rows.map((r) => cols.map((c) => htmlToText(String(r[c] ?? '')))),
        },
      ]
    }
    // Array of plain strings → bullet list (array of empty objects → nothing).
    return value
      .filter((v) => typeof v === 'string' && v.trim())
      .map((v) => ({ type: 'bullet', text: htmlToText(v) }))
  }
  if (typeof value === 'object' && value.url) {
    return [{ type: 'text', text: `${value.caption || value.fileName || 'Attachment'}: ${value.url}` }]
  }
  if (typeof value === 'object' && value.type === 'reactflow' && Array.isArray(value.nodes)) {
    const t = reactflowToText(value)
    return t ? [{ type: 'text', text: t }] : []
  }
  return []
}

/** Turn a SOP `content` blob into an ORDERED list of PDF blocks
 *  ({ type: 'text' | 'heading' | 'bullet' | 'table' }). The text content is
 *  identical to flattenSopContent(); the only structural upgrade is that tables
 *  (array-of-rows sections, and HTML <table>s in the rich body) become `table`
 *  blocks so the PDF draws a real grid. Non-table bodies stay byte-for-byte the
 *  same as the old text export. */
function sopContentToBlocks(content) {
  if (!content || typeof content !== 'object') return []
  const structured = content.structured
  const htmlBody = typeof content.html === 'string' ? content.html : ''

  // Non-template shapes (no structured / flat text / rich TipTap doc): the
  // modal renders content.html, so when that HTML carries a <table> we parse it
  // into blocks (grid); otherwise keep the exact plain_text body as before.
  if (
    !structured ||
    typeof structured !== 'object' ||
    structured.type === 'text' ||
    !isStructuredTemplate(structured)
  ) {
    if (htmlBody.trim() && /<table\b/i.test(htmlBody)) return htmlToBlocks(htmlBody)
    const plain =
      structured && structured.type === 'text' && typeof structured.content === 'string'
        ? structured.content
        : typeof content.plain_text === 'string'
          ? content.plain_text
          : ''
    return plain.trim() ? [{ type: 'text', text: plain.trim() }] : []
  }

  // Structured {header, sections} template — ordered sections, each labelled.
  const blocks = []
  const header =
    structured.header && typeof structured.header === 'object' ? structured.header : null
  if (header) {
    const hp = []
    if (header.sopNumber) hp.push(`SOP Number: ${header.sopNumber}`)
    if (header.version) hp.push(`Version: ${header.version}`)
    if (header.date) hp.push(`Date: ${header.date}`)
    if (header.department) hp.push(`Department: ${header.department}`)
    if (hp.length) blocks.push({ type: 'text', text: hp.join('\n') })
  }
  const sections =
    structured.sections && typeof structured.sections === 'object' ? structured.sections : {}
  const customDefs = Array.isArray(structured.customSectionDefs)
    ? structured.customSectionDefs
    : []
  const orderedSet = new Set(SOP_SECTION_ORDER)
  const allKeys = [
    ...SOP_SECTION_ORDER,
    ...Object.keys(sections).filter((k) => !orderedSet.has(k)),
  ]
  let hasSectionText = false
  for (const key of allKeys) {
    const value = sections[key]
    if (isEmptySection(value)) continue
    const sub = sectionToBlocks(value).filter(
      (b) => b && (b.type !== 'text' || b.text.trim()),
    )
    if (sub.length) {
      blocks.push({ type: 'heading', text: resolveSectionLabel(key, customDefs) })
      blocks.push(...sub)
      hasSectionText = true
    }
  }
  if (!hasSectionText) {
    const plain = typeof content.plain_text === 'string' ? content.plain_text.trim() : ''
    if (plain) blocks.push({ type: 'text', text: plain })
  }
  return blocks
}

/** The metadata lines shown as a muted header block beneath the SOP title. */
function sopMetaLines(sop) {
  const meta = []
  if (sop.departmentName) meta.push(`Department: ${sop.departmentName}`)
  if (sop.status) meta.push(`Status: ${sop.status}`)
  if (sop.priority) meta.push(`Priority: ${sop.priority}`)
  if (sop.category) meta.push(`Category: ${sop.category}`)
  if (sop.version != null) {
    meta.push(
      `Version: ${sop.versionLabel ? `${sop.versionLabel} (v${sop.version})` : `v${sop.version}`}`,
    )
  }
  if (sop.owner?.name) {
    meta.push(
      `Owner: ${sop.owner.email ? `${sop.owner.name} <${sop.owner.email}>` : sop.owner.name}`,
    )
  }
  if (sop.updatedAt) meta.push(`Updated: ${formatDateTime(sop.updatedAt)}`)
  return meta
}

/** Safe `.pdf` filename from a SOP title. */
function sopFileName(title) {
  const base = String(title ?? 'sop')
    .replace(/[^\w.-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80)
  return `${base || 'sop'}.pdf`
}

/** Trigger a browser download of the SOP as a PDF. Renders the title + a muted
 *  metadata header, then the body as ORDERED blocks (sopContentToBlocks): text
 *  / headings / bullets via the jsPDF line writer, and tables as real bordered
 *  grids (renderTable). jsPDF lays out text only — no images — so the export
 *  stays light and never waits on remote assets. Helvetica is Latin-only, so
 *  all text is sanitised to ASCII (pdfSafeText) first. */
function downloadSopFile(sop, contentOverride) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 56
  const maxW = pageW - margin * 2
  let y = margin

  function ensureSpace(h) {
    if (y + h > pageH - margin) {
      doc.addPage()
      y = margin
    }
  }

  function write(text, opts = {}) {
    const {
      size = 10.5,
      style = 'normal',
      color = [38, 38, 38],
      gapBefore = 0,
      gapAfter = 6,
      indent = 0,
    } = opts
    if (gapBefore) y += gapBefore
    doc.setFont('helvetica', style)
    doc.setFontSize(size)
    doc.setTextColor(color[0], color[1], color[2])
    const lineHeight = size * 1.35
    // jsPDF standard fonts only render WinAnsi/Latin-1; sanitise Unicode
    // (arrows, bullets, smart quotes) to ASCII so nothing prints as garbage.
    for (const ln of doc.splitTextToSize(pdfSafeText(text), maxW - indent)) {
      ensureSpace(lineHeight)
      doc.text(ln, margin + indent, y)
      y += lineHeight
    }
    y += gapAfter
  }

  // One wrapped bullet line, with the marker drawn as a filled circle (the "•"
  // glyph is outside the jsPDF standard-font encoding and prints as a box).
  function writeBullet(text) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10.5)
    doc.setTextColor(38, 38, 38)
    const lineHeight = 10.5 * 1.35
    const wrapped = doc.splitTextToSize(pdfSafeText(text), maxW - 16)
    wrapped.forEach((ln, idx) => {
      ensureSpace(lineHeight)
      if (idx === 0) {
        doc.setFillColor(38, 38, 38)
        doc.circle(margin + 3, y - 3, 1.5, 'F')
      }
      doc.text(ln, margin + 16, y)
      y += lineHeight
    })
    y += 3
  }

  // A multi-line text block: each line styled by its markdown prefix
  // (`## ` sub-heading, `- ` bullet, else body copy).
  function writeLines(text) {
    for (const rawLine of String(text).split('\n')) {
      const line = rawLine.replace(/\s+$/, '')
      if (!line.trim()) {
        y += 4
        continue
      }
      if (line.startsWith('## ')) {
        write(line.slice(3), { size: 13, style: 'bold', color: [20, 20, 20], gapBefore: 8, gapAfter: 5 })
      } else if (line.startsWith('- ')) {
        writeBullet(line.slice(2))
      } else {
        write(line)
      }
    }
  }

  // Title + divider rule.
  write(sop.title ?? 'SOP', { size: 19, style: 'bold', color: [17, 17, 17], gapAfter: 6 })
  ensureSpace(12)
  doc.setDrawColor(214)
  doc.setLineWidth(0.8)
  doc.line(margin, y, pageW - margin, y)
  y += 14

  // Muted metadata header.
  const meta = sopMetaLines(sop)
  for (const line of meta) write(line, { size: 9.5, color: [110, 110, 110], gapAfter: 2 })
  if (meta.length) y += 4

  // Summary paragraph.
  if (sop.summary) write(sop.summary)

  // Body — ordered blocks; tables render as real grids, everything else as text.
  for (const block of sopContentToBlocks(contentOverride ?? sop.content)) {
    if (block.type === 'table') {
      y = renderTable(doc, block, {
        x0: margin,
        width: maxW,
        top: y,
        bottomLimit: pageH - margin,
        topMargin: margin,
      })
    } else if (block.type === 'heading') {
      write(block.text, { size: 13, style: 'bold', color: [20, 20, 20], gapBefore: 8, gapAfter: 5 })
    } else if (block.type === 'bullet') {
      writeBullet(block.text)
    } else {
      writeLines(block.text)
    }
  }

  // Tags footer line.
  if (Array.isArray(sop.tags) && sop.tags.length) write(`Tags: ${sop.tags.join(', ')}`)

  // Footer: page numbers, added once the total is known.
  const pages = doc.getNumberOfPages()
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    doc.text(`Page ${p} of ${pages}`, pageW - margin, pageH - 28, {
      align: 'right',
    })
  }

  doc.save(sopFileName(sop.title))
}
